import { useCallback, useRef, useState } from "react";

/**
 * Signature state for a patient admission document.
 *
 * The patient's drawing and the clinician's typed stamp are both pending until
 * the next save, so they are held outside the form's field values: the drawing
 * is sent once and answered with a stored URL, and the typed stamp carries its
 * own editable date. Every admission form needs exactly this, so it lives here
 * rather than in each form.
 */
export default function useFormSignatures() {
  const [patientDrawing, setPatientDrawing] = useState(null);
  const [patientCleared, setPatientCleared] = useState(false);
  const patientPadRef = useRef(null);

  const [clinicianSignature, setClinicianSignature] = useState(null);
  const [clinicianCleared, setClinicianCleared] = useState(false);
  const [clinicianSignedAtOverride, setClinicianSignedAtOverride] =
    useState(null);

  /** Called after every successful load or save, so nothing is resent. */
  const reset = useCallback(() => {
    setPatientDrawing(null);
    setPatientCleared(false);
    setClinicianSignature(null);
    setClinicianCleared(false);
    setClinicianSignedAtOverride(null);
    patientPadRef.current?.reset?.();
  }, []);

  const handlePatientDraw = useCallback((dataUrl) => {
    setPatientDrawing(dataUrl);
    setPatientCleared(false);
  }, []);

  const handlePatientClear = useCallback(() => {
    setPatientDrawing(null);
    setPatientCleared(true);
  }, []);

  const handleClinicianSign = useCallback((signature) => {
    setClinicianSignature({
      text: signature,
      signedAt: new Date().toISOString(),
    });
    setClinicianCleared(false);
  }, []);

  const handleClinicianClear = useCallback(() => {
    setClinicianSignature(null);
    setClinicianCleared(true);
    setClinicianSignedAtOverride(null);
  }, []);

  /**
   * The date is editable but the signature stamp carries a time, so the chosen
   * day is merged onto the existing time rather than replacing it.
   */
  const changeClinicianDate = useCallback(
    (ymd, storedSignedAt) => {
      if (!ymd) {
        setClinicianSignedAtOverride(null);
        return;
      }
      const [year, month, day] = ymd.split("-").map(Number);
      if (!year || !month || !day) return;
      const base =
        clinicianSignedAtOverride ||
        clinicianSignature?.signedAt ||
        storedSignedAt;
      const next = base ? new Date(base) : new Date();
      next.setFullYear(year, month - 1, day);
      setClinicianSignedAtOverride(next.toISOString());
    },
    [clinicianSignature, clinicianSignedAtOverride],
  );

  /** What the clinician has pending, merged onto the payload before a save. */
  const buildSignaturePayload = useCallback(() => {
    const payload = {};
    if (patientDrawing) payload.patientSignatureImage = patientDrawing;
    if (patientCleared) payload.clearPatientSignature = true;
    if (clinicianSignature?.text)
      payload.clinicianSignatureText = clinicianSignature.text;
    if (clinicianSignedAtOverride)
      payload.clinicianSignedAt = clinicianSignedAtOverride;
    if (clinicianCleared) payload.clearClinicianSignature = true;
    return payload;
  }, [
    patientDrawing,
    patientCleared,
    clinicianSignature,
    clinicianCleared,
    clinicianSignedAtOverride,
  ]);

  /**
   * The props `FormSignatureSections` needs, resolved against the saved form so
   * a pending signature shows immediately instead of waiting for the server.
   */
  const sectionProps = useCallback(
    (form) => ({
      patientSignatureUrl: patientCleared ? null : form?.patientSignatureUrl,
      patientSignedAt: form?.patientSignedAt,
      patientPadRef,
      onPatientDraw: handlePatientDraw,
      onPatientClear: handlePatientClear,
      clinicianSignatureText: clinicianCleared
        ? ""
        : clinicianSignature?.text || form?.clinicianSignatureText,
      clinicianSignedAt: clinicianCleared
        ? null
        : clinicianSignedAtOverride ||
          clinicianSignature?.signedAt ||
          form?.clinicianSignedAt,
      onClinicianClear: handleClinicianClear,
      onClinicianDateChange: (ymd) =>
        changeClinicianDate(ymd, form?.clinicianSignedAt),
    }),
    [
      patientCleared,
      clinicianCleared,
      clinicianSignature,
      clinicianSignedAtOverride,
      handlePatientDraw,
      handlePatientClear,
      handleClinicianClear,
      changeClinicianDate,
    ],
  );

  return {
    patientPadRef,
    reset,
    handleClinicianSign,
    buildSignaturePayload,
    sectionProps,
  };
}
